/**
 * Comprehensive spec compliance validator for switchboard routing data
 * Validates that legacy format data matches all requirements from switchboard-routing-data-specification.md
 *
 * Verified Implementation Status (2025-01-27):
 * - ✅ All 23 spec node types are implemented
 * - ✅ All node-specific html fields match the spec exactly
 * - ✅ Backend stores compact JSON (no spaces) - frontend builds it correctly
 * - ✅ Backend routing uses target_uuid.split('-')[1].split('_')[0] for node ID extraction
 * - ✅ All children arrays start with {} for auto_attendant, call_distribution, interactive_webhook
 * - ⚠️ UUID extraction has edge cases (nodeIds with hyphens/underscores, empty nodeIds) but these are unlikely in normal operation
 */
import {
  FIXED_HANDLE_ANCHORS,
  FIXED_HANDLE_LABELS,
  REQUIRED_HTML_FIELDS,
} from './constants/index'
import type { LegacyFormatData, LegacyFormatNode } from './legacyFormatTypes'
import type { LegacyNodeKind } from './legacyNodes'
import { extractNodeIdFromUuid } from './uuidUtils'
import {
  validateEndpointReferences,
  validateTargetEndpointExists,
} from './validators'

const DYNAMIC_OPTION_NODE_KINDS = new Set<LegacyNodeKind>([
  'auto_attendant',
  'call_distribution',
  'interactive_webhook',
])

export interface SpecValidationError {
  type: 'critical' | 'warning'
  nodeId?: string
  nodeKind?: string
  field?: string
  message: string
  messageKey?: string
}

export interface SpecValidationResult {
  valid: boolean
  errors: SpecValidationError[]
  warnings: SpecValidationError[]
}

/**
 * Validates that a legacy format switchboard data structure complies with the routing spec
 */
export function validateSpecCompliance(
  data: LegacyFormatData,
): SpecValidationResult {
  const errors: SpecValidationError[] = []
  const warnings: SpecValidationError[] = []

  // Validate structure
  validateStructure(data, errors)

  // Build node map for reference validation
  const nodesById = new Map<string, LegacyFormatNode>()
  const allNodes: LegacyFormatNode[] = []
  const nodeIdOccurrences = new Map<string, number>() // Track duplicate node IDs

  if (data.nodes.start_node) {
    allNodes.push(data.nodes.start_node)
    const count = nodeIdOccurrences.get(data.nodes.start_node.id) || 0
    nodeIdOccurrences.set(data.nodes.start_node.id, count + 1)
    // Only set in map if first occurrence (to avoid overwriting)
    if (count === 0) {
      nodesById.set(data.nodes.start_node.id, data.nodes.start_node)
    }
  }

  Object.values(data.nodes).forEach((nodeOrArray) => {
    if (Array.isArray(nodeOrArray)) {
      nodeOrArray.forEach((node) => {
        allNodes.push(node)
        const count = nodeIdOccurrences.get(node.id) || 0
        nodeIdOccurrences.set(node.id, count + 1)
        // Only set in map if first occurrence (to avoid overwriting)
        if (count === 0) {
          nodesById.set(node.id, node)
        }
      })
    }
  })

  // Validate for duplicate node IDs
  nodeIdOccurrences.forEach((count, nodeId) => {
    if (count > 1) {
      errors.push({
        type: 'critical',
        nodeId,
        message: `Duplicate node ID found: "${nodeId}" appears ${count} times. Each node ID must be unique within the canvas.`,
      })
    }
  })

  // Validate each node
  allNodes.forEach((node) => {
    validateNode(node, nodesById, errors, warnings)
  })

  // Validate graph integrity
  validateGraphIntegrity(allNodes, nodesById, errors, warnings)

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  }
}

/**
 * Validates the overall structure of the legacy format data
 */
function validateStructure(
  data: LegacyFormatData,
  errors: SpecValidationError[],
): void {
  if (!data.nodes) {
    errors.push({
      type: 'critical',
      message: 'Missing "nodes" object in legacy format data',
      messageKey: 'switchboard.validation.spec.missingNodesObject',
    })
    return
  }

  // Validate start_node is object (not array)
  if (data.nodes.start_node) {
    if (Array.isArray(data.nodes.start_node)) {
      errors.push({
        type: 'critical',
        nodeId: 'start_node',
        message:
          'start_node must be an object, not an array. Structure violation.',
      })
    }
  } else {
    errors.push({
      type: 'critical',
      message: 'Missing start_node. All switchboards must have a start_node.',
      messageKey: 'switchboard.validation.spec.missingStartNode',
    })
  }

  // Validate all other node types are arrays
  Object.entries(data.nodes).forEach(([key, value]) => {
    if (key === 'start_node') {
      return // Already validated
    }

    if (!Array.isArray(value)) {
      errors.push({
        type: 'critical',
        nodeKind: key,
        message: `Node type "${key}" must be an array, not an object. Structure violation.`,
      })
    }
  })
}

/**
 * Validates a single node against spec requirements
 */
function validateNode(
  node: LegacyFormatNode,
  nodesById: Map<string, LegacyFormatNode>,
  errors: SpecValidationError[],
  warnings: SpecValidationError[],
): void {
  const kind = node.kind

  // Validate node has required fields
  if (!node.id) {
    errors.push({
      type: 'critical',
      nodeKind: kind,
      message: `Node missing required "id" field`,
    })
  }

  if (!node.kind) {
    errors.push({
      type: 'critical',
      nodeId: node.id,
      message: `Node missing required "kind" field`,
    })
  }

  // Validate endpoints structure
  if (!node.endpoints) {
    errors.push({
      type: 'critical',
      nodeId: node.id,
      nodeKind: kind,
      message: 'Node missing "endpoints" object',
      messageKey: 'switchboard.validation.spec.missingEndpointsObject',
    })
    return
  }

  if (!Array.isArray(node.endpoints.sources)) {
    errors.push({
      type: 'critical',
      nodeId: node.id,
      nodeKind: kind,
      message: 'Node missing or invalid "endpoints.sources" array',
      messageKey: 'switchboard.validation.spec.missingSourcesArray',
    })
  }

  if (!Array.isArray(node.endpoints.targets)) {
    errors.push({
      type: 'critical',
      nodeId: node.id,
      nodeKind: kind,
      message: 'Node missing or invalid "endpoints.targets" array',
      messageKey: 'switchboard.validation.spec.missingTargetsArray',
    })
  }

  // Validate HTML property (all nodes except start_node must have it)
  if (kind !== 'start_node') {
    if (!('html' in node)) {
      errors.push({
        type: 'critical',
        nodeId: node.id,
        nodeKind: kind,
        message:
          'Node missing required "html" property. All nodes except start_node must have html object.',
      })
    } else if (!node.html || typeof node.html !== 'object') {
      errors.push({
        type: 'critical',
        nodeId: node.id,
        nodeKind: kind,
        message: 'Node "html" property must be an object',
        messageKey: 'switchboard.validation.spec.invalidHtmlObject',
      })
    } else {
      // Validate required HTML fields exist
      validateRequiredHtmlFields(node, errors, warnings)
    }
  } else {
    // start_node should NOT have html
    if ('html' in node) {
      warnings.push({
        type: 'warning',
        nodeId: node.id,
        nodeKind: kind,
        message: 'start_node should not have "html" property',
        messageKey: 'switchboard.validation.spec.startNodeHtmlWarning',
      })
    }
  }

  // Validate fixed handles
  validateFixedHandles(node, errors)

  // Validate dynamic option integrity for option-driven nodes
  validateDynamicOptionIntegrity(node, errors)

  // Validate endpoint references (this also validates UUID format)
  // Note: validateEndpointReferences uses isValidLegacyEndpointUuid which correctly handles
  // node kinds with underscores (e.g., "day_night", "auto_attendant") by splitting from the end
  const endpointValidation = validateEndpointReferences(
    node as unknown as Parameters<typeof validateEndpointReferences>[0],
  )
  if (!endpointValidation.valid) {
    endpointValidation.errors.forEach((err) => {
      errors.push({
        type: 'critical',
        nodeId: node.id,
        nodeKind: kind,
        field: `endpoints.${err.type}[].uuid`,
        message: err.error,
      })
    })
  }
}

/**
 * Validates that all required HTML fields exist for a node type
 */
function validateRequiredHtmlFields(
  node: LegacyFormatNode,
  errors: SpecValidationError[],
  warnings: SpecValidationError[],
): void {
  const kind = node.kind
  const requiredFields = REQUIRED_HTML_FIELDS[kind as LegacyNodeKind]

  if (!requiredFields) {
    return // No required fields defined for this node type
  }

  if (!node.html || typeof node.html !== 'object') {
    return // Already validated as error
  }

  const html = node.html

  Object.keys(requiredFields).forEach((fieldName) => {
    if (!(fieldName in html)) {
      errors.push({
        type: 'critical',
        nodeId: node.id,
        nodeKind: kind,
        field: `html.${fieldName}`,
        message: `Missing required HTML field: ${fieldName}. Field must exist (can be null).`,
      })
    }
  })

  // Special validation for children array format
  if ('children' in html && Array.isArray(html.children)) {
    const children = html.children as Array<unknown>
    if (children.length > 0 && typeof children[0] !== 'object') {
      errors.push({
        type: 'critical',
        nodeId: node.id,
        nodeKind: kind,
        field: 'html.children',
        message:
          'children array must start with empty object [{}]. First element must be an object.',
      })
    } else if (
      children.length > 0 &&
      Object.keys(children[0] as object).length > 0
    ) {
      warnings.push({
        type: 'warning',
        nodeId: node.id,
        nodeKind: kind,
        field: 'html.children',
        message:
          'children array should start with empty object [{}]. First element should be empty.',
      })
    }
  }
}

/**
 * Validates that all required fixed handles exist for nodes that need them
 */
function validateRequiredFixedHandle(
  node: LegacyFormatNode,
  sources: Array<{ my_label?: string; anchor?: string }>,
  kind: LegacyNodeKind,
  label: string,
  expectedAnchor: string,
  errors: SpecValidationError[],
): void {
  const sameLabelHandles = sources.filter((s) => s.my_label === label)

  if (sameLabelHandles.length === 0) {
    errors.push({
      type: 'critical',
      nodeId: node.id,
      nodeKind: kind,
      message: `Missing required fixed handle: "${label}".`,
    })
    return
  }

  const semanticMatches = sameLabelHandles.filter(
    (s) => s.anchor === expectedAnchor,
  )

  if (semanticMatches.length === 0) {
    errors.push({
      type: 'critical',
      nodeId: node.id,
      nodeKind: kind,
      field: `endpoints.sources[].anchor (${label})`,
      message: `Incorrect anchor for "${label}": expected "${expectedAnchor}", got "${sameLabelHandles[0]?.anchor}".`,
    })
  }

  if (semanticMatches.length > 1) {
    errors.push({
      type: 'critical',
      nodeId: node.id,
      nodeKind: kind,
      field: `endpoints.sources[] (${label}, ${expectedAnchor})`,
      message: `Duplicate required fixed handle detected: "${label}" at anchor "${expectedAnchor}" appears ${semanticMatches.length} times.`,
    })
  }
}

function validateFixedHandles(
  node: LegacyFormatNode,
  errors: SpecValidationError[],
): void {
  const kind = node.kind

  if (!node.endpoints?.sources) {
    return // Already validated as error
  }

  const sources = node.endpoints.sources

  // Schedule: 3 handles (Open, Closed, Holiday)
  if (kind === 'schedule') {
    validateRequiredFixedHandle(
      node,
      sources,
      kind,
      FIXED_HANDLE_LABELS.schedule.whenOpen,
      FIXED_HANDLE_ANCHORS.schedule.whenOpen,
      errors,
    )
    validateRequiredFixedHandle(
      node,
      sources,
      kind,
      FIXED_HANDLE_LABELS.schedule.whenClosed,
      FIXED_HANDLE_ANCHORS.schedule.whenClosed,
      errors,
    )
    validateRequiredFixedHandle(
      node,
      sources,
      kind,
      FIXED_HANDLE_LABELS.schedule.holiday,
      FIXED_HANDLE_ANCHORS.schedule.holiday,
      errors,
    )
  }

  // Day/Night: 2 handles (When Open, When Closed)
  if (kind === 'day_night') {
    validateRequiredFixedHandle(
      node,
      sources,
      kind,
      FIXED_HANDLE_LABELS.day_night.whenOpen,
      FIXED_HANDLE_ANCHORS.day_night.whenOpen,
      errors,
    )
    validateRequiredFixedHandle(
      node,
      sources,
      kind,
      FIXED_HANDLE_LABELS.day_night.whenClosed,
      FIXED_HANDLE_ANCHORS.day_night.whenClosed,
      errors,
    )
  }

  // Queue: 2 handles (On Timeout, On Escape)
  if (kind === 'queue') {
    validateRequiredFixedHandle(
      node,
      sources,
      kind,
      FIXED_HANDLE_LABELS.queue.onTimeout,
      FIXED_HANDLE_ANCHORS.queue.onTimeout,
      errors,
    )
    validateRequiredFixedHandle(
      node,
      sources,
      kind,
      FIXED_HANDLE_LABELS.queue.onEscape,
      FIXED_HANDLE_ANCHORS.queue.onEscape,
      errors,
    )
  }

  // Transfer Rollover: 2 handles (No Answer, Busy)
  if (kind === 'transfer_rollover') {
    validateRequiredFixedHandle(
      node,
      sources,
      kind,
      FIXED_HANDLE_LABELS.transfer_rollover.noAnswer,
      FIXED_HANDLE_ANCHORS.transfer_rollover.noAnswer,
      errors,
    )
    validateRequiredFixedHandle(
      node,
      sources,
      kind,
      FIXED_HANDLE_LABELS.transfer_rollover.busy,
      FIXED_HANDLE_ANCHORS.transfer_rollover.busy,
      errors,
    )
  }

  // Auto Attendant: 2 fixed handles (On Timeout, On Invalid)
  if (kind === 'auto_attendant') {
    validateRequiredFixedHandle(
      node,
      sources,
      kind,
      FIXED_HANDLE_LABELS.auto_attendant.onTimeout,
      FIXED_HANDLE_ANCHORS.auto_attendant.onTimeout,
      errors,
    )
    validateRequiredFixedHandle(
      node,
      sources,
      kind,
      FIXED_HANDLE_LABELS.auto_attendant.onInvalid,
      FIXED_HANDLE_ANCHORS.auto_attendant.onInvalid,
      errors,
    )
  }

  // Interactive Webhook: 2 fixed handles (On Timeout, On Invalid)
  if (kind === 'interactive_webhook') {
    validateRequiredFixedHandle(
      node,
      sources,
      kind,
      FIXED_HANDLE_LABELS.interactive_webhook.onTimeout,
      FIXED_HANDLE_ANCHORS.interactive_webhook.onTimeout,
      errors,
    )
    validateRequiredFixedHandle(
      node,
      sources,
      kind,
      FIXED_HANDLE_LABELS.interactive_webhook.onInvalid,
      FIXED_HANDLE_ANCHORS.interactive_webhook.onInvalid,
      errors,
    )
  }

  // AI Agent: 1 handle (On Error)
  if (kind === 'ai_agent_v1') {
    validateRequiredFixedHandle(
      node,
      sources,
      kind,
      'On Error',
      'Right',
      errors,
    )
  }
}

/**
 * Validates graph integrity - all target_uuid references must point to existing target endpoints
 */
function validateGraphIntegrity(
  allNodes: LegacyFormatNode[],
  nodesById: Map<string, LegacyFormatNode>,
  errors: SpecValidationError[],
  warnings: SpecValidationError[],
): void {
  allNodes.forEach((node) => {
    // Handle both array format [{}] and object format {} (defensive)
    const sources = Array.isArray(node.endpoints?.sources)
      ? node.endpoints.sources
      : node.endpoints?.sources
        ? [node.endpoints.sources]
        : []

    if (sources.length === 0) {
      return
    }

    sources.forEach((source) => {
      if (!source.target_uuid) {
        return // Unconnected handle, that's fine
      }

      // Extract node ID from target_uuid using spec formula: split('-')[1].split('_')[0]
      const extractedNodeId = extractNodeIdFromUuid(source.target_uuid)

      if (!extractedNodeId) {
        errors.push({
          type: 'critical',
          nodeId: node.id,
          nodeKind: node.kind,
          field: 'endpoints.sources[].target_uuid',
          message: `Could not extract node ID from target_uuid: ${source.target_uuid}. UUID format may be invalid.`,
        })
        return
      }

      // Find target node
      const targetNode = nodesById.get(extractedNodeId)

      if (!targetNode) {
        errors.push({
          type: 'critical',
          nodeId: node.id,
          nodeKind: node.kind,
          field: 'endpoints.sources[].target_uuid',
          message: `target_uuid references non-existent node ID: ${extractedNodeId}. Extracted from UUID: ${source.target_uuid}`,
        })
        return
      }

      // Validate target endpoint exists
      if (!targetNode.endpoints?.targets) {
        errors.push({
          type: 'critical',
          nodeId: node.id,
          nodeKind: node.kind,
          field: 'endpoints.sources[].target_uuid',
          message: `Target node ${extractedNodeId} has no targets array. Cannot validate target_uuid reference.`,
        })
        return
      }

      const targetExists = validateTargetEndpointExists(
        source.target_uuid,
        targetNode.endpoints,
      )

      // Note: Missing target endpoints are a known legacy bug where source endpoints
      // can reference target UUIDs that don't exist in the target node's targets array.
      // The converter handles this correctly, but we mark it as a warning rather than
      // a critical error since it's a legacy data issue, not a conversion issue.
      if (!targetExists) {
        warnings.push({
          type: 'warning',
          nodeId: node.id,
          nodeKind: node.kind,
          field: 'endpoints.sources[].target_uuid',
          message: `target_uuid ${source.target_uuid} does not reference an existing target endpoint in node ${extractedNodeId}. This is a known legacy bug - the converter will handle it gracefully.`,
        })
      }
    })
  })
}

function validateDynamicOptionIntegrity(
  node: LegacyFormatNode,
  errors: SpecValidationError[],
): void {
  const kind = node.kind as LegacyNodeKind
  if (!DYNAMIC_OPTION_NODE_KINDS.has(kind)) {
    return
  }

  if (!node.html || typeof node.html !== 'object') {
    return
  }

  const children = Array.isArray(node.html.children) ? node.html.children : null
  if (!children) {
    return
  }

  const childOptionIds = new Set<string>()
  const duplicateChildOptionIds = new Set<string>()

  children.slice(1).forEach((child, index) => {
    const childPosition = index + 1
    if (!child || typeof child !== 'object') {
      errors.push({
        type: 'critical',
        nodeId: node.id,
        nodeKind: kind,
        field: `html.children[${childPosition}]`,
        message: 'Dynamic option child must be an object with an "id" field.',
        messageKey: 'switchboard.validation.spec.dynamicOptionChildObject',
      })
      return
    }

    const childObj = child as Record<string, unknown>
    const childIdRaw = childObj.id
    if (typeof childIdRaw !== 'string' || childIdRaw.trim() === '') {
      errors.push({
        type: 'critical',
        nodeId: node.id,
        nodeKind: kind,
        field: `html.children[${childPosition}].id`,
        message: 'Dynamic option child is missing required id.',
        messageKey: 'switchboard.validation.spec.dynamicOptionChildIdMissing',
      })
      return
    }

    const childId = childIdRaw.trim()
    if (childOptionIds.has(childId)) {
      duplicateChildOptionIds.add(childId)
    }
    childOptionIds.add(childId)

    if (
      kind === 'auto_attendant' &&
      (typeof childObj.press_id !== 'string' || childObj.press_id.trim() === '')
    ) {
      errors.push({
        type: 'critical',
        nodeId: node.id,
        nodeKind: kind,
        field: `html.children[${childPosition}].press_id`,
        message: 'Auto attendant option child is missing required press_id.',
        messageKey:
          'switchboard.validation.spec.autoAttendantOptionPressIdMissing',
      })
    }

    if (
      kind === 'call_distribution' &&
      (childObj.weight_id === undefined ||
        childObj.weight_id === null ||
        String(childObj.weight_id).trim() === '')
    ) {
      errors.push({
        type: 'critical',
        nodeId: node.id,
        nodeKind: kind,
        field: `html.children[${childPosition}].weight_id`,
        message:
          'Call distribution option child is missing required weight_id.',
        messageKey:
          'switchboard.validation.spec.callDistributionOptionWeightIdMissing',
      })
    }

    if (
      kind === 'interactive_webhook' &&
      (childObj.option_id === undefined ||
        childObj.option_id === null ||
        String(childObj.option_id).trim() === '')
    ) {
      errors.push({
        type: 'critical',
        nodeId: node.id,
        nodeKind: kind,
        field: `html.children[${childPosition}].option_id`,
        message:
          'Interactive webhook option child is missing required option_id.',
        messageKey:
          'switchboard.validation.spec.interactiveWebhookOptionIdMissing',
      })
    }
  })

  duplicateChildOptionIds.forEach((optionId) => {
    errors.push({
      type: 'critical',
      nodeId: node.id,
      nodeKind: kind,
      field: 'html.children[].id',
      message: `Duplicate dynamic option child id detected: "${optionId}".`,
    })
  })

  const sources = Array.isArray(node.endpoints?.sources)
    ? node.endpoints.sources
    : []
  const optionSources = sources.filter(
    (source) =>
      source &&
      typeof source === 'object' &&
      typeof source.option_id === 'string' &&
      source.option_id.trim() !== '',
  )

  const sourceOptionIds = new Set<string>()
  optionSources.forEach((source) => {
    const optionId = String(source.option_id).trim()
    if (sourceOptionIds.has(optionId)) {
      errors.push({
        type: 'critical',
        nodeId: node.id,
        nodeKind: kind,
        field: 'endpoints.sources[].option_id',
        message: `Duplicate dynamic source endpoint option_id detected: "${optionId}".`,
      })
      return
    }

    sourceOptionIds.add(optionId)

    if (!childOptionIds.has(optionId)) {
      errors.push({
        type: 'critical',
        nodeId: node.id,
        nodeKind: kind,
        field: 'endpoints.sources[].option_id',
        message: `Dynamic source endpoint references unknown option_id "${optionId}" that is not present in html.children.`,
      })
    }
  })

  childOptionIds.forEach((optionId) => {
    if (!sourceOptionIds.has(optionId)) {
      errors.push({
        type: 'critical',
        nodeId: node.id,
        nodeKind: kind,
        field: 'endpoints.sources[].option_id',
        message: `Missing dynamic source endpoint for option child id "${optionId}".`,
      })
    }
  })
}
